import { Component, Input } from '@angular/core';
import { Territory } from '../../../../core/models/territory.interface';
import { catchError, map, Observable, switchMap, tap } from 'rxjs';
import { LayoutService } from '../../../../core/services/layout/layout';
import { ActivatedRoute, Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { ToastrService } from 'ngx-toastr';
import { Title } from '@angular/platform-browser';
import { LoadingService } from '../../../../core/services/loading/loading';
import { TerritoryService } from '../../../../core/services/territory/territory';
import { PAGINATION } from '../../../../core/constants/constants';
import { Filter, QueryGet } from '../../../../core/types/query-types';
import { TerritoryForm } from '../territory-form/territory-form';
import { MatIcon } from '@angular/material/icon';
import {
  MatCell,
  MatCellDef,
  MatColumnDef,
  MatHeaderCell,
  MatHeaderCellDef,
  MatHeaderRow,
  MatHeaderRowDef,
  MatRow,
  MatRowDef,
  MatTable,
} from '@angular/material/table';
import { NgxPaginationModule } from 'ngx-pagination';
import { AsyncPipe } from '@angular/common';
import { OnlysuperuserDirective } from '../../../../shared/directives/onlysuperuser.directive';
import { PermisionDirective } from '../../../../shared/directives/permision.directive';
import { Search } from '../../../../shared/components/search/search';

@Component({
  selector: 'app-territory-list',
  imports: [
    MatIcon,
    MatTable,
    MatColumnDef,
    MatHeaderCellDef,
    MatHeaderCell,
    MatCellDef,
    MatCell,
    MatHeaderRowDef,
    MatHeaderRow,
    MatRowDef,
    MatRow,
    NgxPaginationModule,
    AsyncPipe,
    OnlysuperuserDirective,
    PermisionDirective,
    Search,
  ],
  templateUrl: './territory-list.html',
  styleUrl: './territory-list.scss',
})
export class TerritoryList {
  public page = 1;
  @Input() filtro: string = '';
  @Input() orden: string = '';
  limit = 10;
  total = 0;
  territories: Territory[] = [];
  asyncTerritory!: Observable<Territory[]>;
  selectedTerritory: Territory | null = null;
  showColumns: string[] = [
    'acciones',
    'id',
    'name',
    'nit',
    'zone',
    'resolution',
    'legalRepresentation',
    'lifePlan',
  ];

  constructor(
    private layoutService: LayoutService,
    private activatedRouter: ActivatedRoute,
    private router: Router,
    private dialog: MatDialog,
    private toastr: ToastrService,
    private territoryService: TerritoryService,
    private loadingService: LoadingService,
    private title: Title,
  ) {
    this.title.setTitle('Territorios');
  }

  ngOnInit(): void {
    this.layoutService.paginationLimit$
      .pipe(
        switchMap((limit) => {
          this.limit = limit;
          return this.activatedRouter.queryParamMap;
        }),
        switchMap((params) => {
          const pageNum = params.get('page') || '1';
          const search = params.get('search') || '';
          this.page = parseInt(pageNum);
          this.fetchTerritory(PAGINATION.first_page, null, search);
          return this.layoutService.paginationLimit$;
        }),
      )
      .subscribe();
  }

  fetchTerritory(page: number, filter: Filter | null = null, search = '') {
    const offset = page > 1 ? page * this.limit - this.limit : 0;
    const params: QueryGet = {
      order: '-id',
      limit: this.limit,
      offset,
      search,
      filters: [],
    };
    if (filter) {
      params.filters.push(filter);
    }
    this.page = page;
    this.loadingService.show(true);
    this.asyncTerritory = this.territoryService.getTerritory(params).pipe(
      tap((data) => {
        this.territories = data.results;
        this.territoryService.setTerritory(data.results[0]);
        this.territoryService.setTerritories(data.results);
        this.total = data.count;
      }),
      map((res) => {
        this.loadingService.show(false);
        return res.results;
      }),
      catchError((error) => {
        this.loadingService.show(false);
        const errorMsg = `¡Se ha producido un error, inténtelo nuevamente!`;
        this.toastr.error(errorMsg, 'Error!', {
          progressBar: true,
          timeOut: 5000,
        });
        this.router.navigate(['home']);
        return [];
      }),
    );
  }

  createTerritory() {
    this.territoryService.removeTerritorySelected();
    const dialogRef = this.dialog.open(TerritoryForm, {
      width: '600px',
      height: 'auto',
      maxHeight: '80vh',
      disableClose: true,
    });
    dialogRef.afterClosed().subscribe(() => {
      this.fetchTerritory(this.page);
    });
  }

  updateTerritory(territory: Territory) {
    this.territoryService.setTerritory(territory);
    const dialogRef = this.dialog.open(TerritoryForm, {
      width: '600px',
      height: 'auto',
      maxHeight: '80vh',
      disableClose: true,
    });
    dialogRef.afterClosed().subscribe(() => {
      this.fetchTerritory(this.page);
    });
  }

  eliminate(territory: Territory): void {
    const confirmed = window.confirm(`¿Estás seguro de eliminar el registro: ${territory.name}?`);
    if (!confirmed) return;
    this.territoryService.delete(territory.id).subscribe({
      next: () => {
        const successMsg = `Eliminado CORRECTAMENTE!`;
        this.toastr.success(successMsg, 'Exitoso!', {
          progressBar: true,
          timeOut: 5000,
        });
        this.fetchTerritory(this.page);
      },
      error: (err) => {
        this.toastr.error('No se pudo eliminar', 'Error');
      },
    });
  }
}
